import type { ReactNode } from 'react'
import { Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from './useAuth'
import { PATHS } from '@/lib/routes'

/**
 * Replicates authConfig.callbacks.authorized():
 *   - Unauthenticated → redirect to /login preserving ?feature= (critical for K8s ingress routing).
 *   - loading         → render null (SessionGuard handles the spinner inside AppLayout).
 *   - authenticated   → render children.
 *
 * Public paths that don't require authentication are handled by placing them
 * outside this wrapper in router.tsx.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth()
  const location   = useLocation()
  const [searchParams] = useSearchParams()

  if (status === 'loading') {
    // SessionGuard (inside AppLayout) shows the spinner — nothing to render here
    return null
  }

  if (status === 'unauthenticated') {
    const feature = searchParams.get('feature')
    const loginUrl = feature ? `${PATHS.login}?feature=${encodeURIComponent(feature)}` : PATHS.login
    return <Navigate to={loginUrl} replace state={{ from: location }} />
  }

  return <>{children}</>
}
